import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Plus, Loader2, Trash2, ArrowUp, ArrowDown, Pencil, Eye, X, Save, Globe, FileEdit, Users } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { ManualBlockEditor } from "@/components/manuals/ManualBlockEditor";
import { ManualBlockRenderer } from "@/components/manuals/ManualBlockRenderer";

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

const ASSIGNABLE_ROLES = [
  { id: "manager", label: "Managers" },
  { id: "worker", label: "Workers" },
  { id: "applicant", label: "Applicants" },
];

const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

// ==================== SECTION EDITOR ====================
const SectionEditor = ({ manualId, section, onClose, onSaved }) => {
  const [title, setTitle] = useState(section?.title || "");
  const [blocks, setBlocks] = useState(section?.blocks || []);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (title.trim().length < 2) return toast.error("Give the section a title.");
    setBusy(true);
    try {
      if (section) {
        await axios.patch(`${API}/admin/manuals/${manualId}/sections/${section.id}`, { title: title.trim(), blocks }, { headers: authHeaders() });
        toast.success("Section saved.");
      } else {
        await axios.post(`${API}/admin/manuals/${manualId}/sections`, { title: title.trim(), blocks }, { headers: authHeaders() });
        toast.success("Section created as a draft — publish it when ready.");
      }
      onSaved();
      onClose();
    } catch (e) { err(e, "Could not save the section."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="manual-section-editor" className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm sm:p-8" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-lg font-bold text-[var(--pv-text)]">{section ? "Edit section" : "New section"}</h3>
          <div className="flex items-center gap-2">
            <button data-testid="section-preview-toggle" onClick={() => setPreview((v) => !v)} className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-bold ${preview ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-300" : "border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)]"}`}>
              <Eye className="h-3.5 w-3.5" /> {preview ? "Back to editor" : "Preview"}
            </button>
            <button data-testid="section-editor-close" onClick={onClose} className="rounded-full p-2 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <input data-testid="section-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Section title (e.g. Reviewing Submissions)" className={`${inputCls} mt-4`} />
        <div className="mt-4">
          {preview ? (
            <div data-testid="section-preview" className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
              <ManualBlockRenderer blocks={blocks} />
            </div>
          ) : (
            <ManualBlockEditor blocks={blocks} onChange={setBlocks} />
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button data-testid="section-cancel-btn" onClick={onClose} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-5 py-2.5 text-xs font-bold text-[var(--pv-text-3)]">Cancel</button>
          <button data-testid="section-save-btn" onClick={save} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-6 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {section ? "Save changes" : "Create draft section"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== MANUAL DETAIL (sections + access) ====================
const ManualDetail = ({ manual, onBack, onChanged }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // section object | "new" | null
  const [editMeta, setEditMeta] = useState(false);
  const [meta, setMeta] = useState({ title: manual.title, description: manual.description || "", allowed_roles: manual.allowed_roles || [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/manuals/${manual.id}`, { headers: authHeaders() }).then((r) => setData(r.data)).catch(() => toast.error("Could not load manual."));
  }, [manual.id]);
  useEffect(load, [load]);

  const move = async (section, dir) => {
    const ids = data.sections.map((s) => s.id);
    const i = ids.indexOf(section.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setData((d) => ({ ...d, sections: ids.map((id) => d.sections.find((s) => s.id === id)) }));
    try {
      await axios.put(`${API}/admin/manuals/${manual.id}/sections/order`, { ordered_ids: ids }, { headers: authHeaders() });
    } catch (e) { err(e, "Reorder failed."); load(); }
  };

  const togglePublish = async (section) => {
    try {
      await axios.post(`${API}/admin/manuals/${manual.id}/sections/${section.id}/publish`, { published: section.status !== "published" }, { headers: authHeaders() });
      toast.success(section.status === "published" ? "Section unpublished — readers no longer see it." : "Section published — assigned staff can now read it.");
      load();
      onChanged();
    } catch (e) { err(e, "Could not change publish state."); }
  };

  const removeSection = async (section) => {
    if (!window.confirm(`Delete section "${section.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/manuals/${manual.id}/sections/${section.id}`, { headers: authHeaders() });
      toast.success("Section deleted.");
      load();
      onChanged();
    } catch (e) { err(e, "Delete failed."); }
  };

  const saveMeta = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/manuals/${manual.id}`, meta, { headers: authHeaders() });
      toast.success("Manual settings saved.");
      setEditMeta(false);
      onChanged();
    } catch (e) { err(e, "Could not save settings."); }
    finally { setBusy(false); }
  };

  const sections = data?.sections || [];

  return (
    <div data-testid="manual-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="manual-detail-back" onClick={onBack} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-2 text-[var(--pv-text-3)] hover:text-cyan-400">←</button>
          <div>
            <h2 data-testid="manual-detail-title" className="font-display text-xl font-bold tracking-tight text-[var(--pv-text)]">{manual.title}</h2>
            <p className="text-xs text-[var(--pv-text-4)]">
              Visible to: {(manual.allowed_roles || []).length ? manual.allowed_roles.join(", ") : "nobody yet"}
              {(manual.allowed_user_ids || []).length > 0 && ` + ${manual.allowed_user_ids.length} specific user${manual.allowed_user_ids.length === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="manual-settings-btn" onClick={() => { setMeta({ title: manual.title, description: manual.description || "", allowed_roles: manual.allowed_roles || [] }); setEditMeta((v) => !v); }} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)] hover:text-cyan-400">
            <Users className="h-3.5 w-3.5" /> Title & access
          </button>
          <button data-testid="section-add-btn" onClick={() => setEditing("new")} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
            <Plus className="h-3.5 w-3.5" /> Add section
          </button>
        </div>
      </div>

      {editMeta && (
        <div data-testid="manual-settings-form" className="mt-4 grid gap-3 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="manual-title-input" value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} placeholder="Manual title" className={inputCls} />
            <input data-testid="manual-desc-input" value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} placeholder="Short description (optional)" className={inputCls} />
          </div>
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Who can read this manual</p>
            <div className="flex flex-wrap gap-2">
              {ASSIGNABLE_ROLES.map((r) => {
                const on = meta.allowed_roles.includes(r.id);
                return (
                  <button key={r.id} type="button" data-testid={`manual-access-${r.id}`}
                    onClick={() => setMeta((m) => ({ ...m, allowed_roles: on ? m.allowed_roles.filter((x) => x !== r.id) : [...m.allowed_roles, r.id] }))}
                    className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${on ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-4)]"}`}>
                    {r.label} {on ? "✓" : ""}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[10px] text-[var(--pv-text-4)]">You (the owner) always have full access. Readers get read-only access to published sections only.</p>
          </div>
          <button data-testid="manual-settings-save" onClick={saveMeta} disabled={busy || meta.title.trim().length < 3} className="flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save settings
          </button>
        </div>
      )}

      {!data ? (
        <p className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : sections.length === 0 ? (
        <p data-testid="manual-sections-empty" className="mt-4 rounded-3xl border border-dashed border-[var(--pv-border-strong)] py-14 text-center text-sm text-[var(--pv-text-4)]">
          No sections yet — add your first one. New sections start as drafts, invisible to readers until published.
        </p>
      ) : (
        <div data-testid="manual-sections-list" className="mt-4 space-y-2">
          {sections.map((s, i) => (
            <div key={s.id} data-testid={`manual-section-row-${s.id}`} className="flex flex-wrap items-center gap-2 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--pv-subtle)] text-[10px] font-bold text-[var(--pv-text-4)]">{i + 1}</span>
              <p className="min-w-0 flex-1 truncate text-sm font-bold text-[var(--pv-text)]">{s.title}</p>
              <span className="text-[10px] font-semibold text-[var(--pv-text-4)]">{(s.blocks || []).length} block{(s.blocks || []).length === 1 ? "" : "s"}</span>
              <span data-testid={`section-status-${s.id}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${s.status === "published" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>{s.status}</span>
              <button data-testid={`section-up-${s.id}`} onClick={() => move(s, -1)} disabled={i === 0} className="rounded-lg p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)] hover:text-cyan-400 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button data-testid={`section-down-${s.id}`} onClick={() => move(s, 1)} disabled={i === sections.length - 1} className="rounded-lg p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)] hover:text-cyan-400 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button data-testid={`section-edit-${s.id}`} onClick={() => setEditing(s)} className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] hover:text-cyan-400"><Pencil className="h-3 w-3" /> Edit</button>
              <button data-testid={`section-publish-${s.id}`} onClick={() => togglePublish(s)} className={`flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-bold ${s.status === "published" ? "border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"}`}>
                {s.status === "published" ? <><FileEdit className="h-3 w-3" /> Unpublish</> : <><Globe className="h-3 w-3" /> Publish</>}
              </button>
              <button data-testid={`section-delete-${s.id}`} onClick={() => removeSection(s)} className="rounded-lg p-1.5 text-[var(--pv-text-4)] hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <SectionEditor
          manualId={manual.id}
          section={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); onChanged(); }}
        />
      )}
    </div>
  );
};

// ==================== OWNER MANUALS CONSOLE ====================
export const AdminManuals = () => {
  const [manuals, setManuals] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", allowed_roles: ["manager"] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/manuals`, { headers: authHeaders() }).then((r) => setManuals(r.data)).catch(() => toast.error("Could not load manuals."));
  }, []);
  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/manuals`, form, { headers: authHeaders() });
      toast.success(`Manual "${data.title}" created — add sections next.`);
      setShowAdd(false);
      setForm({ title: "", description: "", allowed_roles: ["manager"] });
      load();
      setSelected(data);
    } catch (e2) { err(e2, "Could not create the manual."); }
    finally { setBusy(false); }
  };

  const removeManual = async (m) => {
    if (!window.confirm(`Delete "${m.title}" and all its sections? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/manuals/${m.id}`, { headers: authHeaders() });
      toast.success("Manual deleted.");
      setSelected(null);
      load();
    } catch (e) { err(e, "Delete failed."); }
  };

  if (selected) {
    const fresh = (manuals || []).find((m) => m.id === selected.id) || selected;
    return (
      <div data-testid="admin-manuals">
        <div className="mb-5 flex items-center justify-between">
          <button data-testid="manual-delete-btn" onClick={() => removeManual(fresh)} className="flex items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-xs font-bold text-rose-300 hover:bg-rose-500/20">
            <Trash2 className="h-3.5 w-3.5" /> Delete manual
          </button>
        </div>
        <ManualDetail manual={fresh} onBack={() => setSelected(null)} onChanged={load} />
      </div>
    );
  }

  return (
    <div data-testid="admin-manuals">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-500/15 text-cyan-300">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Internal Manuals & SOPs</h1>
            <p className="mt-1 text-sm text-[var(--pv-text-4)]">Reusable playbooks for managers, writers and crew — with screenshots, drafts and publish control.</p>
          </div>
        </div>
        <button data-testid="manual-add-btn" onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
          <Plus className="h-3.5 w-3.5" /> New manual
        </button>
      </div>

      {showAdd && (
        <form data-testid="manual-add-form" onSubmit={create} className="mt-5 grid gap-3 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="manual-new-title" required minLength={3} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder='Manual title (e.g. "Production Manager Manual")' className={inputCls} />
            <input data-testid="manual-new-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Short description (optional)" className={inputCls} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Readable by:</span>
            {ASSIGNABLE_ROLES.map((r) => {
              const on = form.allowed_roles.includes(r.id);
              return (
                <button key={r.id} type="button" data-testid={`manual-new-access-${r.id}`}
                  onClick={() => setForm((f) => ({ ...f, allowed_roles: on ? f.allowed_roles.filter((x) => x !== r.id) : [...f.allowed_roles, r.id] }))}
                  className={`rounded-full border px-3.5 py-1.5 text-[11px] font-bold ${on ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-4)]"}`}>
                  {r.label}
                </button>
              );
            })}
          </div>
          <button data-testid="manual-create-btn" type="submit" disabled={busy || form.title.trim().length < 3} className="flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create manual
          </button>
        </form>
      )}

      {!manuals ? (
        <p className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : manuals.length === 0 ? (
        <p data-testid="manuals-empty" className="mt-6 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16 text-center text-sm text-[var(--pv-text-4)]">
          No manuals yet — create your first one (e.g. the Production Manager Manual).
        </p>
      ) : (
        <div data-testid="manuals-list" className="mt-6 grid gap-4 sm:grid-cols-2">
          {manuals.map((m) => (
            <button key={m.id} data-testid={`manual-card-${m.id}`} onClick={() => setSelected(m)} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 text-left transition-colors hover:border-cyan-500/30">
              <div className="flex items-start justify-between gap-3">
                <p className="font-display text-base font-bold text-[var(--pv-text)]">{m.title}</p>
                <BookOpen className="h-4 w-4 shrink-0 text-cyan-400" />
              </div>
              {m.description && <p className="mt-1.5 line-clamp-2 text-xs text-[var(--pv-text-4)]">{m.description}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-2 py-0.5 text-[10px] font-bold text-[var(--pv-text-3)]">{m.published}/{m.sections} published</span>
                {(m.allowed_roles || []).map((r) => (
                  <span key={r} className="rounded-full border border-cyan-500/25 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold capitalize text-cyan-300">{r}s</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
